
import { computed, defineComponent, PropType } from "vue";
import { ElTooltip } from "element-plus";

import i18n from "@/core/i18n/i18n";
import type { WizardContextLevel } from "@/flow/services/wizard.service";
import {
  RING_INACTIVE_COLOR,
  ringColorFor,
} from "@/flow/utils/wizardContextRing";

const { t } = i18n.global;

/**
 * A 20px glyph in a 32px hit area, matching wizard-attach-btn and wizard-send-btn
 * so the composer row stays optically even.
 */
const GLYPH = 20;
const STROKE = 2.5;
const RADIUS = (GLYPH - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * Props are re-declared as an interface and cast in `setup`, mirroring
 * WizardChat.vue. `defineComponent`'s inference resolves `Object as PropType<...>`
 * to `unknown` under this project's TS config, so every sibling wizard component
 * casts rather than relying on it.
 */
interface WizardContextRingProps {
  level: WizardContextLevel | null;
  open: boolean;
  compacting: boolean;
  error: string;
}

export default defineComponent({
  name: "WizardContextRing",
  components: { ElTooltip },
  props: {
    /** Null until the first turn has created a thread to describe. */
    level: {
      type: Object as PropType<WizardContextLevel | null>,
      default: null,
    },
    open: {
      type: Boolean,
      default: false,
    },
    compacting: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: "",
    },
  },
  emits: ["toggle", "close", "compact"],
  setup(props, { emit }) {
    const typedProps = props as WizardContextRingProps;

    const percent = computed(() =>
      Math.round((typedProps.level?.fraction ?? 0) * 100)
    );

    const dashOffset = computed(
      () => CIRCUMFERENCE * (1 - (typedProps.level?.fraction ?? 0))
    );

    const ringColor = computed(() =>
      typedProps.level?.available
        ? ringColorFor(
            typedProps.level.usedTokens,
            typedProps.level.compactAtTokens
          )
        : RING_INACTIVE_COLOR
    );

    /** Where compaction starts, as a share of the bar's width. */
    const thresholdPercent = computed(() => {
      const { level } = typedProps;
      if (!level || level.usableTokens <= 0) return 100;
      return Math.min((level.compactAtTokens / level.usableTokens) * 100, 100);
    });

    const formatNumber = (value: number) => value.toLocaleString();

    /**
     * Payload for `v-outside-click`, which takes an object rather than a callback.
     *
     * Passing a bare function silently broke the close-on-outside-click: the
     * directive reads `binding.value.disabledScopeElements.some(...)` first, so an
     * undefined property threw inside the document listener before it ever reached
     * `handle`. This component is the directive's only consumer in the codebase, so
     * there was no working example to copy and nothing else exercising the shape.
     *
     * `disabledScopeElements` lists selectors whose clicks should NOT count as
     * outside. Empty here: the ring button already stops propagation, so the
     * document listener never sees the toggle click.
     */
    const outsideClick = {
      handle: () => emit("close"),
      disabledScopeElements: [] as string[],
    };

    return {
      t,
      GLYPH,
      STROKE,
      RADIUS,
      CIRCUMFERENCE,
      percent,
      dashOffset,
      ringColor,
      thresholdPercent,
      formatNumber,
      outsideClick,
    };
  },
});
